import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import {
  ArrowLeftRight,
  ChevronDown,
  ChevronRight,
  LayoutGrid,
  LineChart,
} from "lucide-react";
import { GoMegaphone } from "react-icons/go";
import { SlSettings } from "react-icons/sl";
import { TbUsersGroup } from "react-icons/tb";
import { Link, useLocation } from "react-router";
import Logo from "../assets/logo.png";
import { BsBank } from "react-icons/bs";
import { Fragment, useState, useEffect } from "react";

const items = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: <LayoutGrid />,
    isActive: true,
  },
  {
    title: "Balances",
    url: "/dashboard/balances",
    icon: <BsBank />,
    isActive: true,
  },
  {
    title: "Customers",
    url: "/dashboard/customers",
    icon: <TbUsersGroup />,
    isActive: true,
  },
  {
    title: "Transactions",
    url: "/dashboard/transactions",
    icon: <ArrowLeftRight />,
  },
  {
    title: "Markets",
    icon: <LineChart />,
    subItems: [
      {
        title: "Wallex",
        url: "/dashboard/wallex-rates",
      },
      {
        title: "External",
        url: "/dashboard/external-rates",
      },
      {
        title: "Settings",
        url: "/dashboard/markets",
      },
    ],
  },
  {
    title: "Marketing",
    url: "/dashboard/marketing",
    icon: <GoMegaphone />,
  },
  // {
  //   title: "Audit Logs",
  //   url: "#",
  //   icon: <PiBinocularsThin />,
  // },
  {
    title: "Settings",
    url: "/dashboard/settings",
    icon: <SlSettings />,
  },
];

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const path = useLocation()?.pathname;
  const { setOpenMobile } = useSidebar();

  const [marketsOpen, setMarketsOpen] = useState(() => {
    return (
      path.includes("/dashboard/markets") ||
      path.includes("/dashboard/wallex-rates") ||
      path.includes("/dashboard/external-rates")
    );
  });

  useEffect(() => {
    if (
      path.includes("/dashboard/markets") ||
      path.includes("/dashboard/wallex-rates") ||
      path.includes("/dashboard/external-rates")
    ) {
      setMarketsOpen(true);
    }
  }, [path]);

  return (
    <>
      <Sidebar {...props}>
        <SidebarHeader className="h-16 px-4">
          <div className="p-2">
            <Link to="/dashboard" className="w-full">
              <img src={Logo} className=" h-8" />
            </Link>
          </div>

          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild></SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent className="px-4 py-6">
          <SidebarGroup>
            <SidebarMenu className="gap-3">
              {items.map((item) => (
                <Fragment key={item.title}>
                  {item.subItems ? (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => setMarketsOpen(!marketsOpen)}
                        className={cn(
                          "rounded-[8px] px-4 flex justify-between items-center w-full",
                          item.subItems.some((sub) => sub.url === path)
                            ? "bg-primary-0 hover:bg-primary-0 text-primary"
                            : "bg-transparent hover:bg-transparent text-gray"
                        )}
                      >
                        <div className="flex gap-3 items-center">
                          {item.icon}
                          <span className="text-sm font-medium">
                            {item.title}
                          </span>
                        </div>
                        {marketsOpen ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </SidebarMenuButton>
                      {marketsOpen && (
                        <SidebarMenuSub>
                          {item.subItems.map((sub) => (
                            <SidebarMenuSubItem key={sub.title}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={path === sub.url}
                              >
                                <Link
                                  to={sub.url}
                                  className={cn(
                                    "rounded-[8px]",
                                    path === sub.url
                                      ? "text-primary font-medium"
                                      : "text-gray"
                                  )}
                                  onClick={() => setOpenMobile(false)}
                                >
                                  {sub.title}
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      )}
                    </SidebarMenuItem>
                  ) : (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        className={cn(
                          "rounded-[8px] px-4",
                          path === item.url ||
                            (item?.url &&
                              item?.url !== "/dashboard" &&
                              path?.startsWith(item?.url))
                            ? "bg-primary-0 hover:bg-primary-0"
                            : "bg-transparent hover:bg-transparent"
                        )}
                      >
                        <Link
                          to={item.url!}
                          className={cn(
                            "flex gap-3",
                            path === item.url ||
                              (item?.url &&
                                item?.url !== "/dashboard" &&
                                path?.startsWith(item?.url))
                              ? "text-primary"
                              : "text-gray"
                          )}
                          onClick={() => setOpenMobile(false)}
                        >
                          {item?.icon}
                          <span className="text-sm font-medium">
                            {item?.title}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {item?.title === "Dashboard" && <SidebarSeparator />}
                </Fragment>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
    </>
  );
}
